import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, mergeProps as _mergeProps, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, withCtx as _withCtx, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-12" }
const _hoisted_2 = { class: "max-w-176" }
const _hoisted_3 = { class: "w-full pt-3" }
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = {
  key: 0,
  class: "my-2 border-b"
}
const _hoisted_6 = { id: "paymentDetails" }

import { computed, watch, ref, ComputedRef, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { IReviewSummary, ISitecoreFormGenericInput, OwnedAssistanceProduct } from '@/interfaces';
import { useAccountStore, useManageDirectDebitStore, useRoadsideAssistance } from '@/store';
import { getFormInput, getFormInputByName } from '@/utils/form-input-helper';
import { replaceBookmark, replaceBookmarks } from '@/utils/bookmark.replace';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import ReviewSummary from '@/components/ReviewSummary/ReviewSummary.vue';
import { executeEditRules } from '@/utils/edit-rules/execute-rules';
import { RSAEditRules } from '@/utils/self-service-rules/RSAEditRules';
import { RSAFirstLinkRules } from '@/utils/self-service-rules/RSAFirstLinkRules';
import { RSASubcontentRules } from '@/utils/self-service-rules/RSASubcontentRules';
import { executeDisplayRulesNoPolicyWithParams } from '@/utils/display-rules/execute-rules';
import { RSADisplayRules } from '@/utils/self-service-rules/RSADisplayRules';
import { RSAPromoCardRules } from '@/utils/self-service-rules/RSAPromoCardRules';
import { RoadsideProductType, RSA_ROADSIDE_EXCESS_USE } from '@/utils/roadside-helpers';
import { IPromoCardMini } from '@/interfaces/entities/promo-card-mini.interface';
import PromoCardMiniList from '@/components/PromoCardMini/PromoCardMiniList.vue';
import { asyncComputed } from '@vueuse/core';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { IRoadsideContentInterface } from '@/interfaces/self-service/roadside.interface';
import { InformationBoxTypes, IInformationBox } from '@/interfaces/forms/information-box.interface';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import {
  RsaStatusInfoBox,
  RSAStatusInformationBoxRules,
} from '@/utils/self-service-rules/RSAStatusInformationBoxRules';
import { format } from 'date-fns';
import { SHORT_MONTH_DATE, LONG_MONTH_DATE } from '@/constants/formats';
import { ApttusDirectDebitStatus } from '@/interfaces/entities/apttus-direct-debit-status-response.interface';

enum Bookmarks {
  coverExpiryDate = 'coverExpiryDate',
  coverLevel = 'coverLevel',
  vehicleDetails = 'vehicleDetails',
  registrationNumber = 'registrationNumber',
  policyId = 'policyId',
  makePaymentUrl = 'makePaymentUrl',
}

type IExtendedInformationBox = IInformationBox & { firstLinkContent?: string };


export default /*@__PURE__*/_defineComponent({
  __name: 'RsaProductDetails',
  props: {
    content: {},
    id: {},
    name: {}
  },
  setup(__props: any) {

const accountStore = useAccountStore();
const rsaStore = useRoadsideAssistance();
const manageDirectDebitStore = useManageDirectDebitStore();
const route = useRoute();

const props = __props;

const filteredContent = computed(() => {
  return props.content.filter(
    (item) =>
      item.name != 'statusMessages' ||
      (rsaStatusInfoBoxForCurrentPolicy.value !== RsaStatusInfoBox.None && rsaActiveStatusInformationBox.value)
  );
});

const policySummary = getFormInput(props.content, 'coverSummary');
const coverFeatureSummary = getFormInput(props.content, 'coverFeatureSummary');
const paymentInfoSummary = getFormInput(props.content, 'paymentInfo');
const requestRsaPromoCards = getFormInput(props.content, 'requestRsaPromoCards');

const coverSummaries = ref<IReviewSummary[]>([]);
const rsaProductContent = ref<IRoadsideContentInterface>();
const noCoverFeatures = ref<boolean>(false);
const currentPolicyTitle = ref<string>('');
const makePaymentUrl = ref<string>();
const directDebitStatus = ref<ApttusDirectDebitStatus>();
const showPaymentDetails = ref<boolean>(false);

onMounted(async () => {
  await manageDirectDebitStore.setDirectDebitDetailsFromIdentity();
  directDebitStatus.value = manageDirectDebitStore.directDebitStatus;
  const eligibleProducts = await manageDirectDebitStore.getPortfolioAssitanceProductsForDirectDebit();
  showPaymentDetails.value =
    !!currentPolicy.value && eligibleProducts.some((x) => x.assetLineItemId === currentPolicy.value?.assetLineItemId);
  makePaymentUrl.value = await getMakePaymentUrl(currentPolicy.value?.prn ?? '');
});

const currentPolicy = computed(() => {
  if (!accountStore.identityDetails?.portfolio?.assistanceProducts) return null;
  const policyId = route.query.policyId;
  const status = route.query.status;

  const usePolicyId = (policyId?.length ?? 0) > 0 ? policyId : null;
  if (!usePolicyId) return null;

  const tempPolicy = accountStore?.identityDetails?.portfolio?.assistanceProducts.find(
    (x) =>
      x.policyId === usePolicyId && (!status ? true : x.status?.toLowerCase() === status?.toString()?.toLowerCase())
  );

  return tempPolicy;
});

watch(
  () => currentPolicy.value?.prn,
  async (newVal) => {
    if (newVal) {
      makePaymentUrl.value = await getMakePaymentUrl(newVal);
    }
  }
);

const formatItem = (item: any, currentPolicy: OwnedAssistanceProduct) => {
  item.fields.content.value = replaceBookmark(
    Bookmarks.coverExpiryDate,
    item.fields.content.value,
    format(currentPolicy.endDate, SHORT_MONTH_DATE)
  );
  item.fields.content.value = replaceBookmark(
    Bookmarks.coverLevel,
    item.fields.content.value,
    currentPolicyTitle.value
  );
  if (currentPolicy.vehicleDetail) {
    item.fields.content.value = replaceBookmark(
      Bookmarks.vehicleDetails,
      item.fields.content.value,
      `${currentPolicy.vehicleDetail.manufacturingYear} ${currentPolicy.vehicleDetail.make.description} ${currentPolicy.vehicleDetail.model?.description ?? ''} ${currentPolicy.vehicleDetail.body?.colour ?? ''}`
    );
    item.fields.content.value = replaceBookmark(
      Bookmarks.registrationNumber,
      item.fields.content.value,
      currentPolicy.vehicleDetail.registrationNumber
    );
  }
  // Replace policy id in url
  if (item.fields.primaryLink.value?.querystring) {
    item.fields.primaryLink.value.querystring = replaceBookmark(
      Bookmarks.policyId,
      item.fields.primaryLink.value.querystring,
      currentPolicy.policyId!
    );
  }
  if (item.fields.subcontent?.value) {
    item.fields.subcontent.value = replaceBookmark(
      Bookmarks.policyId,
      item.fields.subcontent.value,
      currentPolicy.policyId!
    );
  }
  item.showPrimaryLink = executeEditRules(RSAEditRules, item.fields.editRules, currentPolicy);
  item.showFirstLink = executeEditRules(RSAFirstLinkRules, item.fields.firstLinkRules, currentPolicy);
  item.showSubcontent = executeEditRules(RSASubcontentRules, item.fields.subcontentRules, currentPolicy);

  return item;
};

const formatCoverItem = async (item: any, currentPolicy: OwnedAssistanceProduct) => {
  rsaProductContent.value = await rsaStore.getRsaProductContent(currentPolicy.productCode);
  const content = replaceBookmarks(item.fields.content.value, rsaProductContent.value);
  if (!content || content === '-') {
    return null; // clearly signal to drop this item
  }

  item.fields.content.value = content;
  return item;
};

const showOnlinePromoCard = computed(
  () => currentPolicy.value && RSAPromoCardRules.showOnlinePromoCard(currentPolicy.value as OwnedAssistanceProduct)
);
const showPhonePromoCard = computed(
  () => currentPolicy.value && RSAPromoCardRules.showPhonePromoCard(currentPolicy.value as OwnedAssistanceProduct)
);

const filteredPromoCards = computed(() => {
  let promoCards: Partial<IPromoCardMini & { name: string }>[] = requestRsaPromoCards.fields;
  if (promoCards.length > 0) {
    if (!showOnlinePromoCard.value) {
      promoCards = promoCards.filter((p) => p.name?.toLowerCase() !== 'onlineform');
    }

    if (!showPhonePromoCard.value) {
      promoCards = promoCards.filter((p) => p.name?.toLowerCase() !== 'phone');
    }
  }

  return promoCards;
});

const summaryItems = asyncComputed(async () => {
  if (!currentPolicy.value) return [];

  currentPolicyTitle.value =
    currentPolicy.value?.roadsidePolicyType !== RoadsideProductType.Cmo
      ? (currentPolicy.value?.tierDescription ?? '')
      : ((await rsaStore.getRsaProductContent(currentPolicy.value?.productCode))?.tierDescription ?? '');

  return policySummary.fields.map((summary: any) => {
    return {
      ...summary,
      items: summary.items
        .filter((item: any) =>
          executeDisplayRulesNoPolicyWithParams(
            RSADisplayRules,
            item.fields.displayRules,
            currentPolicy.value!,
            directDebitStatus.value!
          )
        )
        .map((item: any) => formatItem(item, currentPolicy.value!)),
    };
  });
});

const getMakePaymentUrl = async (prn: string) => {
  const selfServiceMakePaymentUrl = await accountStore.getSelfServiceMakePaymentUrl();
  return `${selfServiceMakePaymentUrl?.replace('{0}', prn)}`;
};

const rsaStatusInfoBoxForCurrentPolicy = computed(() => {
  return RSAStatusInformationBoxRules.getRsaStatusInfoBox(currentPolicy.value as OwnedAssistanceProduct);
});

const rsaActiveStatusInformationBox: ComputedRef<IExtendedInformationBox | undefined> = computed(() => {
  const statusMessages = getFormInputByName(props.content, 'statusMessages');
  const statusInfoBoxInput = getFormInput(statusMessages.fields, rsaStatusInfoBoxForCurrentPolicy.value);
  if (statusInfoBoxInput) {
    return {
      title: statusInfoBoxInput?.title?.value,
      description: replaceBookmark(
        Bookmarks.coverExpiryDate,
        statusInfoBoxInput?.content?.value,
        format(currentPolicy.value?.endDate ?? '', LONG_MONTH_DATE)
      ),
      type: statusInfoBoxInput?.messageType?.value,
      isCircle: true,
      useButtonSlot: [RsaStatusInfoBox.RenewalPaymentDue, RsaStatusInfoBox.PaymentOverdue].includes(
        rsaStatusInfoBoxForCurrentPolicy.value
      ),
      firstLinkContent: replaceBookmark(
        Bookmarks.makePaymentUrl,
        statusInfoBoxInput?.firstLinkContent?.value,
        makePaymentUrl.value ?? ''
      ),
    };
  }

  return undefined;
});

const mapCoverSummaryItems = async (summaries: any) => {
  if (!currentPolicy.value) return [];

  const resolved = await Promise.all(
    summaries.map(async (summary: any) => {
      const items = (
        await Promise.all(summary.items.map((item: any) => formatCoverItem(item, currentPolicy.value!)))
      ).filter(Boolean); // remove null/undefined items

      return { ...summary, items };
    })
  );
  if (rsaProductContent.value?.infoText && resolved[0].items?.length <= 0) {
    noCoverFeatures.value = true;
    resolved[0].items.push({}); //to show icon on the review summary component
  } else {
    noCoverFeatures.value = false;
  }
  return resolved;
};

const mapPaymentItems = (summaries: any) => {
  if (!currentPolicy.value) return [];

  return summaries.map((summary: any) => {
    return {
      ...summary,
      items: summary.items
        .map((item: any) => {
          return {
            ...item,
            showPrimaryLink: executeEditRules(RSAEditRules, item.fields.editRules, currentPolicy.value!),
          };
        })
        .filter((item: any) =>
          executeDisplayRulesNoPolicyWithParams(
            RSADisplayRules,
            item.fields.displayRules,
            currentPolicy.value!,
            directDebitStatus.value!
          )
        ),
    };
  });
};

const pushAnalytics = () => {
  const pageAnalytics = new PageAnalyticsBuilder('Roadside product details');
  pageAnalytics.populateRsaProductData(currentPolicy.value!);
  pageAnalytics.pushAnalytics();
};

watch(
  currentPolicy,
  async (policy: OwnedAssistanceProduct | null | undefined, oldValue) => {
    if (!policy) {
      coverSummaries.value = [];
      return;
    }
    !oldValue && pushAnalytics();
    coverSummaries.value = await mapCoverSummaryItems(coverFeatureSummary.fields);
  },
  { immediate: true } // if policy is already set when component mounts, run immediately
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(FormStepInputs, {
      step: {
        stepName: _ctx.name,
        formInputs: filteredContent.value,
      }
    }, _createSlots({
      requestRsaPromoCards: _withCtx(() => [
        _createVNode(PromoCardMiniList, _mergeProps(_unref(requestRsaPromoCards), { promoCards: filteredPromoCards.value }), null, 16, ["promoCards"])
      ]),
      statusMessages: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          (rsaActiveStatusInformationBox.value)
            ? (_openBlock(), _createBlock(InformationBox, _normalizeProps(_mergeProps({ key: 0 }, rsaActiveStatusInformationBox.value)), _createSlots({ _: 2 }, [
                (rsaActiveStatusInformationBox.value.firstLinkContent)
                  ? {
                      name: "buttons",
                      fn: _withCtx(() => [
                        _createElementVNode("div", _hoisted_3, [
                          _createElementVNode("span", {
                            innerHTML: rsaActiveStatusInformationBox.value.firstLinkContent
                          }, null, 8, _hoisted_4)
                        ])
                      ]),
                      key: "0"
                    }
                  : undefined
              ]), 1040))
            : _createCommentVNode("", true)
        ])
      ]),
      coverSummary: _withCtx(() => [
        _createVNode(ReviewSummary, {
          reviewSummaries: _unref(summaryItems),
          isConfirmationStep: false,
          disableExpandToggle: false,
          showDiscounts: true,
          title: _unref(policySummary).title,
          isSelfService: true,
          heading: _unref(policySummary).heading,
          belowHeading: _unref(policySummary).belowHeading,
          subheading: _unref(policySummary).subheading
        }, null, 8, ["reviewSummaries", "title", "heading", "belowHeading", "subheading"])
      ]),
      coverFeatureSummary: _withCtx(() => [
        (
            currentPolicy.value?.roadsidePolicyType != _unref(RoadsideProductType).Cmo &&
            currentPolicy.value?.productCode !== _unref(RSA_ROADSIDE_EXCESS_USE)
          )
          ? (_openBlock(), _createBlock(ReviewSummary, {
              key: 0,
              reviewSummaries: coverSummaries.value,
              isConfirmationStep: false,
              disableExpandToggle: false,
              showDiscounts: true,
              title: _unref(coverFeatureSummary).title,
              isSelfService: true,
              subheading: _unref(coverFeatureSummary).subheading
            }, _createSlots({ _: 2 }, [
              (rsaProductContent.value?.infoText)
                ? {
                    name: "policyDiscounts",
                    fn: _withCtx(() => [
                      (!noCoverFeatures.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", {
                        class: _normalizeClass(["mx-5 md:mx-8 mb-9", noCoverFeatures.value ? '-mt-8' : 'mt-5'])
                      }, [
                        _createVNode(InformationBox, {
                          description: rsaProductContent.value?.infoText,
                          type: _unref(InformationBoxTypes).INFO,
                          hideIcon: true
                        }, null, 8, ["description", "type"])
                      ], 2)
                    ]),
                    key: "0"
                  }
                : undefined
            ]), 1032, ["reviewSummaries", "title", "subheading"]))
          : _createCommentVNode("", true)
      ]),
      _: 2
    }, [
      (
          showPaymentDetails.value &&
          (currentPolicy.value?.roadsidePolicyType === _unref(RoadsideProductType).Standard ||
            currentPolicy.value?.productCode?.startsWith('ARN')) &&
          currentPolicy.value?.productCode !== _unref(RSA_ROADSIDE_EXCESS_USE)
        )
        ? {
            name: "paymentInfo",
            fn: _withCtx(() => [
              _createElementVNode("div", _hoisted_6, [
                _createVNode(ReviewSummary, {
                  reviewSummaries: mapPaymentItems(_unref(paymentInfoSummary).fields),
                  isConfirmationStep: false,
                  disableExpandToggle: false,
                  showDiscounts: true,
                  title: _unref(paymentInfoSummary).title,
                  isSelfService: true,
                  heading: _unref(paymentInfoSummary).heading,
                  belowHeading: _unref(paymentInfoSummary).belowHeading,
                  subheading: _unref(paymentInfoSummary).subheading
                }, null, 8, ["reviewSummaries", "title", "heading", "belowHeading", "subheading"])
              ])
            ]),
            key: "0"
          }
        : undefined
    ]), 1032, ["step"])
  ]))
}
}

})